import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DashboardService } from './dashboard.service';

/** Per-channel usage entry for the GLOB-1867 counters UI. */
export interface ChannelUsageEntry {
  channel: string;
  label: string;
  queries: number;
  leads: number;
}

/** Subset of the /bot/:id/conversation_stats response this service reads. */
interface ConversationStatsResponse {
  web_messages_count?: number;
  wsp_messages_count?: number;
  ig_messages_count?: number;
  fb_messages_count?: number;
}

interface LeadChannelTotal {
  channel: string;
  total: number;
}

/** Channels surfaced in the counters UI, matching GLOB-1867's AC ("por IG/WhatsApp/FB/web"). */
const CHANNELS: { key: string; label: string }[] = [
  { key: 'WEB', label: 'Web' },
  { key: 'WHATSAPP', label: 'WhatsApp' },
  { key: 'INSTAGRAM', label: 'Instagram' },
  { key: 'MESSENGER', label: 'Messenger' },
];

/**
 * GLOB-1867 — Per-channel queries + leads breakdown for the usage counters UI.
 *
 * Data sources (pinned by the AC):
 * - Queries: DashboardService.getConversationStats — the same LIVE, bot-scoped,
 *   per-channel dashboard endpoint already used elsewhere. NOT platform_metrics
 *   (global, no per-account breakdown).
 * - Leads: DashboardService.getLeads filtered by channel (one call per
 *   bot x channel, page_size=1, reading only `.total`) — the same LIVE leads
 *   listing endpoint already used by the Leads UI. NOT quota_counter (no
 *   channel dimension).
 *
 * Both counts are aggregated across all of the caller's chatbots and scoped
 * to the given [periodStart, periodEnd] window (the owner's rolling
 * quota_counter cycle), so the breakdown adds up to the same cycle shown by
 * the cap/consumption counter.
 */
@Injectable({
  providedIn: 'root',
})
export class UsageCounterService {
  constructor(private dashboardService: DashboardService) {}

  getChannelBreakdown(
    botIds: string[],
    periodStart: string,
    periodEnd: string
  ): Observable<ChannelUsageEntry[]> {
    if (!botIds.length) {
      return of(this.emptyBreakdown());
    }

    const dateFrom = this.toDateOnly(periodStart);
    const dateTo = this.toDateOnly(periodEnd);

    const queries$ = forkJoin(
      botIds.map((botId) =>
        this.dashboardService
          .getConversationStats(botId, dateFrom, dateTo)
          .pipe(catchError(() => of(null)))
      )
    );

    const leads$ = forkJoin(
      botIds.flatMap((botId) =>
        CHANNELS.map((c) =>
          this.dashboardService
            .getLeads(botId, {
              channel: c.key,
              date_from: dateFrom,
              date_to: dateTo,
              page: 1,
              page_size: 1,
            })
            .pipe(
              map((res): LeadChannelTotal => ({ channel: c.key, total: res?.total ?? 0 })),
              catchError(() => of<LeadChannelTotal>({ channel: c.key, total: 0 }))
            )
        )
      )
    );

    return forkJoin([queries$, leads$]).pipe(
      map(([statsPerBot, leadsEntries]) =>
        this.aggregate(statsPerBot as (ConversationStatsResponse | null)[], leadsEntries)
      )
    );
  }

  private aggregate(
    statsPerBot: (ConversationStatsResponse | null)[],
    leadsEntries: LeadChannelTotal[]
  ): ChannelUsageEntry[] {
    const queryTotals: Record<string, number> = { WEB: 0, WHATSAPP: 0, INSTAGRAM: 0, MESSENGER: 0 };
    for (const stats of statsPerBot) {
      if (!stats) continue;
      queryTotals['WEB'] += stats.web_messages_count ?? 0;
      queryTotals['WHATSAPP'] += stats.wsp_messages_count ?? 0;
      queryTotals['INSTAGRAM'] += stats.ig_messages_count ?? 0;
      queryTotals['MESSENGER'] += stats.fb_messages_count ?? 0;
    }

    const leadTotals: Record<string, number> = { WEB: 0, WHATSAPP: 0, INSTAGRAM: 0, MESSENGER: 0 };
    for (const entry of leadsEntries) {
      leadTotals[entry.channel] = (leadTotals[entry.channel] ?? 0) + entry.total;
    }

    return CHANNELS.map((c) => ({
      channel: c.key,
      label: c.label,
      queries: queryTotals[c.key] ?? 0,
      leads: leadTotals[c.key] ?? 0,
    }));
  }

  private emptyBreakdown(): ChannelUsageEntry[] {
    return CHANNELS.map((c) => ({ channel: c.key, label: c.label, queries: 0, leads: 0 }));
  }

  private toDateOnly(isoDate: string): string {
    return new Date(isoDate).toISOString().slice(0, 10);
  }
}
